import React from 'react';
import classNames from 'classnames';
import { RuleCompareOperator } from '@bringg/types';
import { Condition, getConditionTestId, trashIcon } from '../rule/rule-utils';
import IndexedLabel from '../../../components/indexed-label/indexed-label';
import AttributePath from '../rule/components/attribute-path';
import { Button, Select, Tooltip } from '../../../components';
import { OPERATOR_SELECT_WIDTH } from '../numeric/single-numeric-condition';
import MultiSelect, { MultiSelectOption } from '../../../components/multi-select/multi-select';
import { INPUT_WIDTH } from '../multi-select/multi-select-condition';

import styles from './array-condition.module.scss';

export type ArrayTranslations = {
	hasOne: string;
	hasAll: string;
	operatorPlaceholder: string;
	valuePlaceHolder: string;
};

interface ArrayConditionProps {
	condition: Condition;
	disabled?: boolean;
	index?: number;
	maxAttributePathItems?: number;
	translations: ArrayTranslations;
	removeTooltipText: string;
	options: Partial<MultiSelectOption>[];
	onChange(condition: Condition, value: number[]): void;
	onRemove(condition: Condition): void;
	withIndexedLabel?: boolean;
}

const ArrayCondition = ({
	translations,
	disabled,
	condition,
	index,
	maxAttributePathItems,
	removeTooltipText,
	withIndexedLabel = true,
	options,
	onRemove,
	onChange
}: ArrayConditionProps) => {
	const { name, value } = condition;
	const testId = getConditionTestId(name);

	const operators = [
		{ name: translations.hasOne, id: RuleCompareOperator.ARRAY_INTERSECTION_HAS_ONE, dropDownKey: 'has_one' },
		{ name: translations.hasAll, id: RuleCompareOperator.ARRAY_INTERSECTION_HAS_ALL, dropDownKey: 'has_all' }
	];

	const handleOperatorChanged = operator => {
		onChange(
			{
				...condition,
				operator
			},
			condition.value as number[]
		);
	};

	return (
		<div
			className={classNames('condition-container', { 'disabled-condition': disabled })}
			data-test-id={getConditionTestId(name)}
		>
			{index && withIndexedLabel && <IndexedLabel index={index} />}
			<div id={!withIndexedLabel ? 'short-name' : undefined} className="name">
				<AttributePath path={name} maxPathItems={maxAttributePathItems} />
			</div>
			<div className={styles.arrayCondition}>
				<Select
					disabled={disabled}
					className="array-select-operator"
					data-test-id="array-select-operator"
					options={operators}
					onChange={handleOperatorChanged}
					placeholder={translations.operatorPlaceholder}
					value={condition.operator}
					allowClear={true}
					style={{ width: OPERATOR_SELECT_WIDTH }}
				/>
				<MultiSelect
					disabled={disabled}
					data-test-id={`${testId}_select`}
					showArrow
					style={{ width: INPUT_WIDTH }}
					placeholder={translations.valuePlaceHolder}
					options={options}
					value={value}
					allowClear
					byKey
					onChange={values => onChange(condition, values)}
				/>
			</div>

			<Tooltip placement="top" title={removeTooltipText}>
				<Button
					shape="circle"
					className="remove-button"
					icon={trashIcon}
					onClick={() => onRemove(condition)}
					data-test-id={`remove-${condition.path}-button`}
				/>
			</Tooltip>
		</div>
	);
};

export default ArrayCondition;
